import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '@/lib/api';
import { toast } from '@/lib/toast';

const TRIGGERS = [
  { value: 'lead_created', label: 'When a new lead is created' },
  { value: 'appointment_booked', label: 'When an appointment is booked' },
  { value: 'appointment_reminder', label: 'Before an appointment (reminder)' },
  { value: 'appointment_followup', label: 'After an appointment (follow-up)' },
];

// Triggers that send immediately — no offset to configure.
const IMMEDIATE_TRIGGERS = new Set(['lead_created', 'appointment_booked']);

const CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

const CONFIRM_STATUSES = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In Progress' },
];

const UNITS = [
  { value: 1, label: 'minutes' },
  { value: 60, label: 'hours' },
  { value: 1440, label: 'days' },
];

const PLACEHOLDER_HELP =
  'Placeholders: {{member_name}}, {{appointment_time}}, {{service_name}}, {{location_name}}, {{business_name}},' +
  ' {{calendar_link}} (add-to-calendar .ics) — check-ins may also use {{confirmation_link}}.';

const LEAD_PLACEHOLDER_HELP =
  'Placeholders: {{member_name}}, {{business_name}} — appointment placeholders are not available for new-lead messages.';

const splitOffset = (minutes) => {
  if (minutes > 0 && minutes % 1440 === 0) return { value: minutes / 1440, unit: 1440 };
  if (minutes > 0 && minutes % 60 === 0) return { value: minutes / 60, unit: 60 };
  return { value: minutes, unit: 1 };
};

const triggerSummary = (a) => {
  if (a.trigger === 'lead_created') return 'On new lead';
  if (a.trigger === 'appointment_booked') return 'On booking';
  const { value, unit } = splitOffset(a.offset_minutes);
  const unitLabel = UNITS.find((u) => u.value === unit)?.label || 'minutes';
  return a.trigger === 'appointment_reminder'
    ? `${value} ${unitLabel} before start`
    : `${value} ${unitLabel} after end`;
};

const blankForm = {
  name: '',
  enabled: true,
  channel: 'email',
  trigger: 'appointment_reminder',
  offsetValue: 24,
  offsetUnit: 60,
  subject: '',
  body: '',
  require_reply: false,
  confirm_status: 'confirmed',
};

const AutomationManager = () => {
  const [automations, setAutomations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/automations');
      setAutomations(data);
    } catch {
      toast.error('Failed to load automations');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm);
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    const { value, unit } = splitOffset(a.offset_minutes);
    setEditing(a);
    setForm({
      name: a.name,
      enabled: a.enabled,
      channel: a.channel,
      trigger: a.trigger,
      offsetValue: value,
      offsetUnit: unit,
      subject: a.subject || '',
      body: a.body || '',
      require_reply: a.require_reply,
      confirm_status: a.confirm_status || 'confirmed',
    });
    setDialogOpen(true);
  };

  const handleToggle = async (a) => {
    try {
      await api.put(`/automations/${a.automation_id}`, { enabled: !a.enabled });
      load();
    } catch {
      toast.error('Failed to update automation');
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete automation "${a.name}"?`)) return;
    try {
      await api.delete(`/automations/${a.automation_id}`);
      toast.success('Automation deleted');
      load();
    } catch {
      toast.error('Failed to delete automation');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Give the automation a name');
      return;
    }
    const isReminder = form.trigger === 'appointment_reminder';
    const payload = {
      name: form.name.trim(),
      enabled: form.enabled,
      channel: form.channel,
      trigger: form.trigger,
      offset_minutes: IMMEDIATE_TRIGGERS.has(form.trigger)
        ? 0
        : Math.max(0, Number(form.offsetValue) || 0) * form.offsetUnit,
      subject: form.subject,
      body: form.body,
      require_reply: isReminder ? form.require_reply : false,
      confirm_status: isReminder && form.require_reply ? form.confirm_status : null,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/automations/${editing.automation_id}`, payload);
        toast.success('Automation updated');
      } else {
        await api.post('/automations', payload);
        toast.success('Automation created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error((Array.isArray(detail) ? detail[0]?.msg : detail) || 'Failed to save automation');
    } finally {
      setSaving(false);
    }
  };

  const isReminder = form.trigger === 'appointment_reminder';
  const showOffset = !IMMEDIATE_TRIGGERS.has(form.trigger);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Automatic messages around appointments — confirmations, reminders and follow-ups. Check-in
        reminders include a confirmation link that updates the appointment when the client taps it.
      </Typography>

      {automations.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No automations yet.
        </Typography>
      )}

      {automations.map((a, idx) => (
        <Stack
          key={a.automation_id}
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ py: 1, borderBottom: idx === automations.length - 1 ? 0 : 1, borderColor: 'divider' }}
        >
          <Switch
            size="small"
            checked={a.enabled}
            onChange={() => handleToggle(a)}
            inputProps={{ 'aria-label': `Enable ${a.name}` }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600} noWrap>
                {a.name}
              </Typography>
              {a.require_reply && (
                <Chip label="check-in" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10.5 }} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {triggerSummary(a)} · {a.channel.toUpperCase()}
              {a.require_reply ? ` · confirms → ${a.confirm_status}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => openEdit(a)} aria-label="Edit automation">
            <EditIcon sx={{ fontSize: 17 }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(a)} aria-label="Delete automation">
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Stack>
      ))}

      <Box>
        <Button startIcon={<AddIcon />} onClick={openCreate} variant="outlined" size="small" data-testid="add-automation-button">
          Add automation
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit automation' : 'New automation'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Name"
                fullWidth
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="automation-name-input"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  />
                }
                label="Enabled"
                sx={{ flexShrink: 0 }}
              />
            </Stack>
            <TextField
              select
              label="Trigger"
              fullWidth
              value={form.trigger}
              onChange={(e) => setForm({ ...form, trigger: e.target.value })}
            >
              {TRIGGERS.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            {showOffset && (
              <Stack direction="row" spacing={1}>
                <TextField
                  label={isReminder ? 'Send before start' : 'Send after end'}
                  type="number"
                  sx={{ flex: 1 }}
                  value={form.offsetValue}
                  onChange={(e) => setForm({ ...form, offsetValue: e.target.value })}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  select
                  label="Unit"
                  sx={{ width: 140 }}
                  value={form.offsetUnit}
                  onChange={(e) => setForm({ ...form, offsetUnit: Number(e.target.value) })}
                >
                  {UNITS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            )}
            <TextField
              select
              label="Channel"
              fullWidth
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
            >
              {CHANNELS.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Subject"
              fullWidth
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={4}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              helperText={form.trigger === 'lead_created' ? LEAD_PLACEHOLDER_HELP : PLACEHOLDER_HELP}
            />
            {isReminder && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.require_reply}
                    onChange={(e) => setForm({ ...form, require_reply: e.target.checked })}
                    data-testid="automation-require-reply"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Require confirmation (check-in)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Adds a confirmation link to the message
                    </Typography>
                  </Box>
                }
              />
            )}
            {isReminder && form.require_reply && (
              <TextField
                select
                label="On confirmation, move appointment to"
                fullWidth
                value={form.confirm_status}
                onChange={(e) => setForm({ ...form, confirm_status: e.target.value })}
              >
                {CONFIRM_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} data-testid="automation-submit-button">
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create automation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AutomationManager;
